import React from 'react';
import { db } from './database';

interface SyncConfig {
  apiUrl: string;
  authToken?: string;
  onProgress?: (progress: number, message: string) => void;
  onConflict?: (local: any, remote: any) => any;
}

class SyncService {
  private syncInProgress = false;
  private syncInterval: number | null = null;
  private config: SyncConfig = {
    apiUrl: process.env.REACT_APP_API_URL || '',
  };
  
  constructor() {
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    
    // Listen for service worker sync events
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_COMPLETE') {
          this.handleSyncComplete(event.data.count);
        }
      });
    }
  }
  
  configure(config: Partial<SyncConfig>): void {
    this.config = { ...this.config, ...config };
  }
  
  private async getAuthToken(): Promise<string | null> {
    const token = this.config.authToken || (await db.getSetting<string>('authToken'));
    return token || null;
  }
  
  private async handleOnline(): Promise<void> {
    console.log('Network connection restored');
    
    // Start auto-sync
    this.startAutoSync();
    
    // Trigger immediate sync
    await this.sync();
  }
  
  private handleOffline(): void {
    console.log('Network connection lost');
    
    // Stop auto-sync
    this.stopAutoSync();
  }
  
  private handleSyncComplete(count: number): void {
    console.log(`Sync completed: ${count} items synchronized`);
    
    // Notify UI components
    window.dispatchEvent(new CustomEvent('sync-complete', {
      detail: { count, timestamp: new Date() }
    }));
  }
  
  startAutoSync(intervalMs: number = 60000): void {
    if (this.syncInterval) {
      return; // Already running
    }
    
    // Only sync when online
    if (navigator.onLine) {
      this.syncInterval = window.setInterval(() => {
        if (navigator.onLine && !this.syncInProgress) {
          this.sync().catch(console.error);
        }
      }, intervalMs);
    }
  }
  
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }
  
  async sync(): Promise<{ success: boolean; syncedItems: number; errors: string[] }> {
    if (!navigator.onLine) {
      return { success: false, syncedItems: 0, errors: ['No network connection'] };
    }
    
    if (this.syncInProgress) {
      return { success: false, syncedItems: 0, errors: ['Sync already in progress'] };
    }
    
    this.syncInProgress = true;
    const errors: string[] = [];
    let syncedItems = 0;
    
    try {
      // Get auth token
      const authToken = await this.getAuthToken();
      if (!authToken) {
        throw new Error('No authentication token available');
      }
      
      // Get pending sync items
      const pendingItems = await db.getPendingSyncItems();
      const total = pendingItems.length;
      
      if (total === 0) {
        return { success: true, syncedItems: 0, errors: [] };
      }
      
      // Process each item
      for (let i = 0; i < pendingItems.length; i++) {
        const item = pendingItems[i];
        
        try {
          await this.syncItem(item, authToken);
          await db.markSyncItemComplete(item.id);
          syncedItems++;
          
          // Report progress
          if (this.config.onProgress) {
            const progress = ((i + 1) / total) * 100;
            this.config.onProgress(progress, `Synced ${i + 1} of ${total} items`);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Failed to sync ${item.type} in ${item.collection}: ${errorMessage}`);
          await db.markSyncItemFailed(item.id, errorMessage);
        }
      }
      
      // Pull remote changes
      await this.pullRemoteChanges(authToken);
      
      return { success: errors.length === 0, syncedItems, errors };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown sync error';
      errors.push(errorMessage);
      return { success: false, syncedItems, errors };
      
    } finally {
      this.syncInProgress = false;
    }
  }
  
  private async syncItem(item: any, authToken: string): Promise<void> {
    const endpoint = `${this.config.apiUrl}/sync/${item.collection}`;
    
    const response = await fetch(endpoint, {
      method: item.type === 'delete' ? 'DELETE' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'X-Sync-Action': item.type,
      },
      body: JSON.stringify(item.data),
    });
    
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Sync failed: ${response.status} - ${error}`);
    }
    
    // Handle conflict resolution
    if (response.status === 409) {
      const remoteData = await response.json();
      const resolvedData = this.config.onConflict
        ? this.config.onConflict(item.data, remoteData)
        : remoteData; // Default: remote wins
      
      // Retry with resolved data
      const retryResponse = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-Sync-Action': 'resolve',
        },
        body: JSON.stringify(resolvedData),
      });
      
      if (!retryResponse.ok) {
        throw new Error('Conflict resolution failed');
      }
    }
  }
  
  private async pullRemoteChanges(authToken: string): Promise<void> {
    try {
      // Get last sync timestamp
      const lastSync = await db.getSetting<string>('lastSyncTimestamp');
      const params = lastSync ? `?since=${lastSync}` : '';
      
      const response = await fetch(`${this.config.apiUrl}/sync/changes${params}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch remote changes: ${response.status}`);
      }
      
      const changes = await response.json();
      
      // Apply remote changes
      for (const change of changes.documents || []) {
        await db.saveDocument(change);
      }
      
      for (const change of changes.extractions || []) {
        await db.saveExtraction(change);
      }
      
      // Update last sync timestamp
      await db.setSetting('lastSyncTimestamp', new Date().toISOString());
      
    } catch (error) {
      console.error('Failed to pull remote changes:', error);
      // Don't throw - pulling is best effort
    }
  }
  
  // Register background sync
  async registerBackgroundSync(): Promise<void> {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if ('sync' in registration) {
        try {
          await (registration as any).sync.register('sync-extractions');
          console.log('Background sync registered');
        } catch (error) {
          console.error('Background sync registration failed:', error);
        }
      }
    }
  }
  
  // Check sync status
  async getSyncStatus(): Promise<{
    online: boolean;
    lastSync: Date | null;
    pendingItems: number;
    inProgress: boolean;
  }> {
    const lastSyncStr = await db.getSetting<string>('lastSyncTimestamp');
    const pendingItems = await db.getPendingSyncItems();
    
    return {
      online: navigator.onLine,
      lastSync: lastSyncStr ? new Date(lastSyncStr) : null,
      pendingItems: pendingItems.length,
      inProgress: this.syncInProgress,
    };
  }
  
  // Manual conflict resolution
  async resolveConflict(
    localId: string,
    resolution: 'local' | 'remote' | 'merge',
    mergedData?: any
  ): Promise<void> {
    // Implementation depends on conflict tracking mechanism
    // This is a placeholder for manual conflict resolution UI
    console.log('Resolving conflict:', { localId, resolution, mergedData });
  }
}

// Export singleton instance
export const syncService = new SyncService();

// Sync status hook for React components
export function useSyncStatus() {
  const [status, setStatus] = React.useState<Awaited<ReturnType<typeof syncService.getSyncStatus>>>();
  
  React.useEffect(() => {
    // Initial status
    syncService.getSyncStatus().then(setStatus);
    
    // Update on sync complete
    const handleSyncComplete = () => {
      syncService.getSyncStatus().then(setStatus);
    };
    
    window.addEventListener('sync-complete', handleSyncComplete);
    window.addEventListener('online', handleSyncComplete);
    window.addEventListener('offline', handleSyncComplete);
    
    // Poll for updates
    const interval = setInterval(() => {
      syncService.getSyncStatus().then(setStatus);
    }, 5000);
    
    return () => {
      window.removeEventListener('sync-complete', handleSyncComplete);
      window.removeEventListener('online', handleSyncComplete);
      window.removeEventListener('offline', handleSyncComplete);
      clearInterval(interval);
    };
  }, []);
  
  return status;
}